/**
 * @fileoverview Outreach History Modal Component
 * @module components/modals/OutreachHistoryModal
 * @description A modal component for displaying the history of outreach dispositions for a member.
 */

import React, { useState, useEffect } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Popover,
  Button,
  TextField,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PropTypes from "prop-types";
// Note icon is an image to match Add Disposition design
import NotesIcon from "../../assets/note-icon.svg";
import { getMemberDispositions } from "../../api/disposition";
import { capitalizeFirstLetter } from "../../utils/capitalize";
import { formatDateOnly, formatTimeOnly } from "../../utils/formatDateTime";

import { useLoading } from "../../context/LoadingContext";

/**
 * OuttreachHistoryModal Component
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Controls modal visibility
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {string} props.memberId - ID of the member
 * @returns {JSX.Element} The rendered modal component
 */
const OutreachHistoryModal = ({ open, onClose, memberId }) => {
  const [history, setHistory] = useState([]);
  const [error, setError] = useState(null);
  const [historyNoteAnchor, setHistoryNoteAnchor] = useState(null);
  const [activeHistoryNote, setActiveHistoryNote] = useState("");
  const [activeHistoryId, setActiveHistoryId] = useState(null);
  const { showLoading, hideLoading } = useLoading();

  useEffect(
    () => {
      const run = async () => {
        try {
          showLoading();
          setError(null);

          const data = await getMemberDispositions(memberId);

          setHistory(data || []);
        } catch (err) {
          hideLoading();
          setError("Failed to load outreach history");
          console.error(err);
        } finally {
          hideLoading();
        }
      };
      if (open && memberId) {
        void run();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open, memberId],
  );

  const handleHistoryNoteClick = (event, content, id) => {
    setHistoryNoteAnchor(event.currentTarget);
    setActiveHistoryNote(content || "");
    setActiveHistoryId(id);
  };

  const handleHistoryNoteClose = () => {
    setHistoryNoteAnchor(null);
    setActiveHistoryNote("");
    setActiveHistoryId(null);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            width: {
              xs: "95vw",
              sm: "90vw",
              md: "90vw",
              lg: "80vw",
              xl: "70vw",
            },
            borderRadius: "16px",
            backgroundColor: "#F7F8FB",
            maxHeight: "90vh",
            m: 1,
          },
        },
      }}
    >
      <Box sx={{ position: "relative", p: 2 }}>
        <DialogTitle
          sx={{
            p: 0,
            m: 0,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "20px",
              color: "#000000",
            }}
          >
            Outreach History
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{ color: "#403C6F" }}
            size="small"
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            p: 0,
            backgroundColor: "#FFFFFF",
            borderRadius: "12px",
            border: "1px solid #E0E0E0",
            overflowX: "hidden", // Change from hidden if we want the full dialog content to scroll, but usually inner scrolling is preferred
            minHeight: "400px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {error ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flex: 1,
                color: "error.main",
              }}
            >
              {error}
            </Box>
          ) : (
            <>
              <TableContainer
                sx={{
                  maxHeight: "60vh",
                  p: 2,
                  pt: 0,
                  overflowY: "auto",
                  overflowX: "auto", // Allow horizontal scrolling for the table
                  position: "relative",
                  mt: 2,
                  width: "100%", // Explicit width needed for overflowX to work properly in flex container
                }}
              >
                <Table
                  stickyHeader
                  size="small"
                  sx={{ minWidth: "850px", tableLayout: "auto" }}
                >
                  <TableHead>
                    <TableRow
                      sx={{
                        position: "sticky",
                        top: 0,
                        zIndex: 5000,
                        backgroundColor: "#CFCEDA",
                        boxShadow: "0 2px 0 rgba(0,0,0,0.05)",
                      }}
                    >
                      {[
                        { label: "Date & Time", width: "11%" },
                        { label: "Care Guide", width: "14%" },
                        { label: "Program", width: "12%" },
                        { label: "Medication", width: "18%" },
                        { label: "Outreach Method", width: "10%" },
                        { label: "Outreach Outcome", width: "30%" },
                        { label: "", width: "5%" }, // Space for notes icon
                      ].map((header) => (
                        <TableCell
                          key={header.label}
                          sx={{
                            backgroundColor: "#CFCEDA",
                            color: "#403C6F",
                            borderColor: "#f5f5f8ff",
                            borderWidth: "0px 1px 0px 1px ",
                            borderStyle: "solid",
                            fontWeight: 700,
                            fontSize: "14px",
                            py: 1.5,
                            borderBottom: "none",
                            whiteSpace: "nowrap",
                            width: header.width,
                            position: "sticky",
                            top: 0,
                            zIndex: 5000,
                          }}
                        >
                          {header.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {history?.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={7}
                          align="center"
                          sx={{
                            py: 10,
                            color: "#66638C",
                            fontSize: "14px",
                            borderBottom: "none",
                          }}
                        >
                          No outreach history available.
                        </TableCell>
                      </TableRow>
                    ) : (
                      history?.map((item, index) => {
                        const dateStr = item.updatedAt;

                        const rawNote =
                          item.note ||
                          item.notes ||
                          item.dispositionNotes ||
                          item.disposition_notes ||
                          item.descriptions ||
                          item.details ||
                          "";
                        const noteStr = String(rawNote).trim();
                        const subCategoryFull = String(
                          item?.subCategoryDisplayName || "",
                        ).trim();
                        const hasSubCategory = Boolean(subCategoryFull);
                        const outcomeColor = item?.colourCode;
                        const outcomeDisplayText =
                          item?.outcomeDisplayName || "";
                        const isNoteActive =
                          historyNoteAnchor &&
                          activeHistoryId === (item.id || `history-${index}`);

                        return (
                          <TableRow
                            key={item.id || index}
                            sx={{
                              "&:hover": { backgroundColor: "#F7F8FB" },
                              "& td": { borderBottom: "1px solid #E0E0E0" },
                            }}
                          >
                            {/* Date & Time */}
                            <TableCell
                              sx={{
                                color: "#403C6F",
                                fontSize: "14px",
                                whiteSpace: "nowrap",
                                borderColor: "#E8E8E9",
                                borderWidth: "0px 1px 0px 1px ",
                                borderStyle: "solid",
                                fontWeight: 600,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                }}
                              >
                                <span>{formatDateOnly(dateStr)}</span>
                                <span
                                  style={{
                                    fontSize: "12px",
                                    fontWeight: 400,
                                    color: "#66638C",
                                  }}
                                >
                                  {formatTimeOnly(dateStr)}
                                </span>
                              </Box>
                            </TableCell>

                            {/* Care Guide */}
                            <TableCell
                              sx={{
                                color: "#403C6F",
                                fontWeight: 600,
                                fontSize: "14px",
                                whiteSpace: "nowrap",
                                borderColor: "#E8E8E9",
                                borderWidth: "0px 0.5px 0px 0.5px ",
                                borderStyle: "solid",
                              }}
                            >
                              {item.careguideName || "N/A"}
                            </TableCell>

                            {/* Program */}
                            <TableCell
                              sx={{
                                color: "#403C6F",
                                fontWeight: 600,
                                fontSize: "13px",
                                whiteSpace: "normal",
                                wordBreak: "break-word",
                                borderColor: "#E8E8E9",
                                borderWidth: "0px 0.5px 0px 0.5px",
                                borderStyle: "solid",
                              }}
                            >
                              {item.programName}
                            </TableCell>

                            {/* Medication */}
                            <TableCell
                              sx={{
                                color: "#403C6F",
                                fontWeight: 400,
                                fontSize: "13px",
                                borderColor: "#E8E8E9",
                                borderWidth: "0px 0.5px 0px 0.5px ",
                                borderStyle: "solid",
                                verticalAlign: "top",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: "4px",
                                }}
                              >
                                <Box
                                  sx={{
                                    fontWeight: 600,
                                    lineHeight: 1.3,
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {capitalizeFirstLetter(
                                    item.drugName ||
                                      item.drug_name ||
                                      item.medicationName ||
                                      item.medication_name ||
                                      "",
                                  )}
                                </Box>
                                {(item.drugNdc ||
                                  item.drug_ndc ||
                                  item.ndc) && (
                                  <Box
                                    sx={{
                                      fontSize: "11px",
                                      color: "#66638C",
                                      whiteSpace: "noWrap",
                                    }}
                                  >
                                    NDC:{" "}
                                    <Box
                                      component="span"
                                      sx={{
                                        fontWeight: 600,
                                        color: "#403C6F",
                                      }}
                                    >
                                      {item.drugNdc ||
                                        item.drug_ndc ||
                                        item.ndc}
                                    </Box>
                                  </Box>
                                )}
                              </Box>
                            </TableCell>

                            {/* Outreach Method */}
                            <TableCell
                              sx={{
                                color: "#403C6F",
                                fontWeight: 600,
                                fontSize: "14px",
                                whiteSpace: "nowrap",
                                borderColor: "#E8E8E9",
                                borderWidth: "0px 0.5px 0px 0.5px ",
                                borderStyle: "solid",
                              }}
                            >
                              {(() => {
                                const outreachMethod =
                                  item.outreachMethodDisplayName ||
                                  item.outreach_method ||
                                  item.contactMethod ||
                                  item.contact_method ||
                                  item.method ||
                                  "";
                                return outreachMethod === "SMS"
                                  ? outreachMethod
                                  : capitalizeFirstLetter(outreachMethod);
                              })()}
                            </TableCell>

                            {/* Outreach Outcome */}
                            <TableCell
                              sx={{
                                fontSize: "14px",
                                fontWeight: 600,
                                borderColor: "#E8E8E9",
                                borderWidth: "0px 0.5px 0px 0.5px",
                                borderStyle: "solid",
                                width: "30%",
                                color: outcomeColor,
                              }}
                            >
                              {hasSubCategory ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "flex-start",
                                    gap: "2px",
                                  }}
                                >
                                  <span
                                    style={{
                                      fontWeight: 600,
                                      fontSize: "14px",
                                      color: outcomeColor,
                                      whiteSpace: "normal",
                                      wordBreak: "break-word",
                                      width: "100%",
                                    }}
                                  >
                                    {capitalizeFirstLetter(outcomeDisplayText)}
                                  </span>
                                  <Tooltip
                                    title={subCategoryFull}
                                    arrow
                                    placement="top"
                                  >
                                    <span
                                      style={{
                                        fontWeight: 400,
                                        fontSize: "12px",
                                        color: "#403C6F",
                                        whiteSpace: "normal",
                                        wordBreak: "break-word",
                                        width: "100%",
                                      }}
                                    >
                                      {` (${subCategoryFull})`}
                                    </span>
                                  </Tooltip>
                                </Box>
                              ) : (
                                <Box
                                  sx={{
                                    whiteSpace: "normal",
                                    wordBreak: "break-word",
                                    color: outcomeColor,
                                  }}
                                >
                                  {capitalizeFirstLetter(outcomeDisplayText)}
                                </Box>
                              )}
                            </TableCell>
                            {/* Notes */}
                            <TableCell
                              align="center"
                              sx={{
                                borderColor: "#E8E8E9",
                                borderWidth: "0px 0.5px 0px 0.5px ",
                                borderStyle: "solid",
                                zIndex: isNoteActive ? 2201 : "auto",
                                position: isNoteActive ? "relative" : "static",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  background: "transparent",
                                  transition:
                                    "background 0.2s, box-shadow 0.2s",
                                  position: "relative",
                                  zIndex: isNoteActive ? 2201 : "auto",
                                  m: 0,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    boxShadow: isNoteActive
                                      ? "0 0 10px rgba(64,60,111,0.6)"
                                      : "none",
                                    transition: "all 0.3s ease-in-out",
                                  }}
                                >
                                  <IconButton
                                    size="small"
                                    disabled={!noteStr}
                                    onClick={(e) =>
                                      handleHistoryNoteClick(
                                        e,
                                        noteStr,
                                        item.id || `history-${index}`,
                                      )
                                    }
                                    sx={{
                                      padding: "4px",
                                      color: "#433ba0ff",
                                      "&:hover": {
                                        backgroundColor:
                                          "rgba(67, 59, 160, 0.08)",
                                      },
                                      "&.Mui-disabled": {
                                        opacity: 0.4,
                                        cursor: "not-allowed",
                                      },
                                    }}
                                  >
                                    <img
                                      src={NotesIcon}
                                      alt="Notes"
                                      style={{
                                        width: "18px",
                                        height: "18px",
                                        opacity: noteStr ? 1 : 0.9,
                                      }}
                                    />
                                  </IconButton>
                                </Box>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Popover
                open={Boolean(historyNoteAnchor)}
                anchorEl={historyNoteAnchor}
                onClose={handleHistoryNoteClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                slotProps={{
                  backdrop: {
                    invisible: false,
                    sx: { backgroundColor: "rgba(27, 27, 27, 0.3)" },
                  },
                  paper: {
                    sx: {
                      p: 2,
                      mt: 0.4,
                      width: 350,
                      backgroundColor: "#F7F8FB",
                      height: "290px",
                      zIndex: 1000,
                      borderRadius: "12px",
                      boxShadow: "0px 4px 12px rgba(0,0,0,0.15)",
                    },
                  },
                }}
              >
                <Box sx={{ position: "relative" }}>
                  <TextField
                    multiline
                    minRows={2}
                    value={activeHistoryNote || ""}
                    fullWidth
                    variant="outlined"
                    size="small"
                    sx={{
                      backgroundColor: "#FFFFFF",
                      width: "100%",
                      minHeight: "200px",
                      borderRadius: "8px",
                      boxShadow: "0px 0px 2px 0px #00000040",
                      overflow: "auto",
                      "& .MuiOutlinedInput-root": {
                        height: "100%",
                        alignItems: "flex-start",
                        padding: 0,
                        "& textarea": {
                          padding: "16px",
                          boxSizing: "border-box",
                          overflow: "auto !important",
                          maxHeight: "calc(200px - 32px)",
                        },
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                      },
                    }}
                    slotProps={{
                      input: {
                        readOnly: true,
                      },
                    }}
                  />
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    mt: 1,
                    gap: 1,
                  }}
                >
                  <Button
                    onClick={handleHistoryNoteClose}
                    variant="outlined"
                    sx={{
                      height: 38,
                      borderRadius: "52px",
                      border: "1px solid #9F9DB6",
                      color: "#403C6F",
                    }}
                  >
                    Close
                  </Button>
                </Box>
              </Popover>
            </>
          )}
        </DialogContent>
      </Box>
    </Dialog>
  );
};

OutreachHistoryModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  memberId: PropTypes.string,
};

export default OutreachHistoryModal;
